import axios from 'axios'
import { serializeParams } from '@/helpers/queryParams'

// Criteria the documents api filters on (src/handlers/documents/list.js, buildFilter). A document
// name and a creation date range are not offered by the list view, precisely because the api
// cannot apply them: nothing is sent that the api would silently ignore.
const SUPPORTED_FILTERS = new Set(['entity_type', 'entity_id', 'type'])

const DEFAULT_OPTIONS = {
  page: 1,
  itemsPerPage: 15,
  sortBy: 'created_at',
  sortDesc: true,
}

/**
 * Tells whether a filter value carries nothing worth sending: a blank string, an empty array,
 * or a nullish value. Kept out of the query params so the api never receives an empty criterion.
 *
 * @param {*} value - The filter value to check.
 * @returns {boolean} True when the value is empty.
 */
function isEmptyValue(value) {
  if (Array.isArray(value)) {
    return value.length === 0
  }
  if (typeof value === 'string') {
    return value.trim() === ''
  }
  return value === null || value === undefined
}

export class DocumentService {
  constructor(auth) {
    this.auth = auth
    this.accessToken = auth?.accessToken
    this.tokenRefreshing = null

    this.documentAPI = axios.create({
      baseURL: process.env.VUE_APP_API_URL,
      timeout: 29900,
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${this.accessToken}`,
      },
      paramsSerializer: serializeParams,
    })

    this.documentAPI.interceptors.request.use(
      (config) => this.refreshTokenIfNeeded(config),
      (error) => Promise.reject(error)
    )
  }

  async refreshTokenIfNeeded(config) {
    if (this.auth.expiresAt - 30000 > Date.now()) {
      return config
    }
    if (!this.tokenRefreshing) {
      this.tokenRefreshing = this.refreshToken()
    }
    await this.tokenRefreshing
    config.headers.Authorization = `Bearer ${this.accessToken}`
    return config
  }

  async refreshToken() {
    try {
      await this.auth.isAuthenticated()
      this.accessToken = this.auth.accessToken
      this.documentAPI.defaults.headers.Authorization = `Bearer ${this.accessToken}`
    } finally {
      this.tokenRefreshing = null
    }
  }

  /**
   * Lists the documents of the tenant, restricted server-side to the caller's locations in store view.
   *
   * `type` and `entity_type` accept a multi-select array, sent as repeated params (see
   * `serializeParams`) because the tenant configuration stores its default filters as arrays. The
   * api reads every value of a repeated key from `multiValueQueryStringParameters`.
   *
   * @param {Object} options - The data table options (page, itemsPerPage, sortBy, sortDesc).
   * @param {Array<Object>} filters - The advanced filters emitted by the FilterDialog.
   * @param {string|null} locationId - The store the list is restricted to, null on the central view.
   * @returns {Promise<{data: Array<Object>, nbDocuments: number}>} The page and the total count.
   */
  async list(options = {}, filters = [], locationId = null) {
    const sortBy = Array.isArray(options.sortBy) ? options.sortBy[0] : options.sortBy
    const sortDesc = Array.isArray(options.sortDesc) ? options.sortDesc[0] : options.sortDesc
    // The api body is already `{ data, nbDocuments }`: it is returned as it comes, so an
    // unexpected body fails in the caller, which reports it, instead of being normalised into an
    // empty page that reads as "no document".
    const response = await this.documentAPI.get('/v1/int/documents', {
      params: {
        ...this.buildFilterParams(filters),
        // Store view: last, so the store scope always wins over a user-supplied criterion. The api
        // checks the location against the caller's own ones, so the restriction cannot be bypassed
        // from here — sending it only narrows the store view to the store selected in the drawer,
        // where the api would otherwise return every location the user is allowed to see.
        ...(locationId ? { location_id: locationId } : {}),
        page: options.page || DEFAULT_OPTIONS.page,
        itemsPerPage: options.itemsPerPage || DEFAULT_OPTIONS.itemsPerPage,
        sortBy: sortBy || DEFAULT_OPTIONS.sortBy,
        sortDesc: typeof sortDesc === 'boolean' ? sortDesc : DEFAULT_OPTIONS.sortDesc,
      },
    })

    return response.data
  }

  async listByEntity(entityType, entityId) {
    const response = await this.documentAPI.get('/v1/int/documents', {
      params: {
        entity_type: entityType,
        entity_id: entityId,
        itemsPerPage: false,
      },
    })

    // Same rule as list(): the page is unwrapped because a caller listing one entity has no use
    // for the count, but nothing is coerced -- an unexpected body must fail, not read as "none".
    return response.data.data
  }

  /**
   * Keeps the active filters the api knows how to apply. Its `buildFilter` reads entity_type,
   * entity_id and type, and nothing else: sending an unsupported criterion would be silently
   * ignored and return an unfiltered page as if it had been applied.
   * Empty values (blank strings, empty arrays) never reach the query params either.
   *
   * @param {Array<Object>} filters - The advanced filters emitted by the FilterDialog.
   * @returns {Object} The query parameters to add.
   */
  buildFilterParams(filters) {
    const params = {}
    for (const filter of filters) {
      if (!filter.active) {
        continue
      }
      if (SUPPORTED_FILTERS.has(filter.name) && !isEmptyValue(filter.value)) {
        params[filter.name] = filter.value
      }
    }
    return params
  }
}
